import {OrbitControls} from "@react-three/drei";
import {useFrame, useThree} from "@react-three/fiber";
import {createXRStore, DefaultXRControllerOptions, XR, XROrigin, XRStoreOptions} from "@react-three/xr";
import * as TWEEN from "@tweenjs/tween.js";
import React, {useEffect, useMemo, useState} from "react";
import * as THREE from "three";
import {RunInAction, useForceUpdate} from "web-vcore";
import {store} from "../../../../Store/index.js";
import {LHQFull, TestStr} from "../../../../Store/main.js";
import {useSceneClickWithRaycastHandler} from "../../../../Utils/ThreeJS/3JSHooks.js";
import {observer_mgl_r3} from "../../../../Utils/ThreeJS/General.js";
import {ProgressiveLightMap} from "../../../../Utils/ThreeJS/ProgressiveLightMap.js";
import {Vec3} from "../../../../Utils/ThreeJS/VectorStructs.js";
import {XRController} from "../../../../Utils/ThreeJS/XR/Controller.js";
import {TeleportTarget} from "../../../../Utils/ThreeJS/XR/Teleport.js";
import {VRHeadsetStateTracker} from "../../../../Utils/ThreeJS/XR/VRHeadsetStateTracker.js";
import {LoadLightmapFromServer} from "../../../@Shared/NavBar/TestingPanel/LightmapSettingsUI.js";
import {MapControls} from "../../../Maps/MapUI/MapSurface/MapControls.js";
import {DreamPanels} from "../3DComps/DreamPanel.js";
import {ImageFrames} from "../3DComps/ImageFrames/ImageFrames.js";
import {LHQOverlay} from "../3DComps/LightmapOverlay.js";
import {PaintableSurface} from "../3DComps/PaintableSurface.js";
import {AlarmClockCatcherSet} from "../3DComps/RCEffects/AlarmClockCatcher.js";
import {ChaoticSphereBoundsSet} from "../3DComps/RCEffects/ChaoticSphereBounds.js";
import {CoinCatcherSet} from "../3DComps/RCEffects/CoinCatcher.js";
import {WaterStreams} from "../3DComps/RCEffects/WaterStreams.js";
import {SceneXREventListeners} from "../3DComps/SceneXREventListeners.js";
import {VRUIRoot} from "../UI/VRUIRoot.js";
import {PaintController} from "../Utils/PaintController.js";
import {LHQBalcony, LHQBookshelves, LHQCeiling, LHQConferenceTable, LHQDesksAndGlassWalls, LHQDoors, LHQElectronics, LHQFloor, LHQPainting, LHQSofaSet, LHQWalls, LHQWindows} from "./3DComps/@Simple3DComps.js";
import {RoomColliders} from "./3DComps/RoomColliders.js";
import {BakedLHQ} from "./3DComps/@BakedLHQComps.js";
import {ImageFramesDebugProbe} from "../3DComps/ImageFrames/ImageFramesDebugProbe.js";
import {ZUpGroupHolder} from "./ZUpGroup.js";
import {SkyBackground} from "./SkyBackground.js";
import {ApplyLightmapIntensity, BakedSun, ClearBakedLightmapMats, LoadBakedSun} from "./BakedLightmaps.js";
import {lightColor, LightmapState, lightPositions_avg} from "./ExtraSetup.js";
import {useFrame_lhqScene} from "./UseFrame.js";

export const inVRHeadset_real = navigator.userAgent.includes("OculusBrowser") || navigator.userAgent.includes("; Quest");
export let inVRHeadset_overrideForTesting: boolean|n = null;
inVRHeadset_overrideForTesting = true;
export function InVRHeadset(allowOverrideForTesting = true)  {
	if (allowOverrideForTesting && inVRHeadset_overrideForTesting != null) return inVRHeadset_overrideForTesting;
	return inVRHeadset_real;
}

const storeOpts: XRStoreOptions = {
	hand: {teleportPointer: true},
	controller: {
		// commented all; these are now applied as checks within the XRController comp (Controller.tsx), so it can adjust to controller-swap settings
		/*left: {
			// atm, left-hand has no use for ray-pointer, so hide to keep it out of the way
			// commented; we need the ray-pointer for interaction events (eg. onPointerOver events attached to objects); instead, we just make its opacity 0 (see Controller.tsx)
			//rayPointer: false,
			teleportPointer: true,
		},
		right: {
			// we use custom ray-pointer (using line instead of 3d ray) for right-hand (see PaintController.tsx)
			// commented; we need the ray-pointer for interaction events (eg. onPointerOver events attached to objects); instead, we just make its opacity 0 (see Controller.tsx)
			//rayPointer: false,
		}*/
	},
	emulate: {
		syntheticEnvironment: false,
	},
};
const xrStore = createXRStore(storeOpts);
//store.setController(XRController);
xrStore.setController(XRController_Wrapped_Left, "left");
xrStore.setController(XRController_Wrapped_Right, "right");
function XRController_Wrapped_Left() {
	const options = storeOpts.controller!["left"] as DefaultXRControllerOptions; // fsr we need to grab it ourselves from what we went initially... (I thought its data would be sent as props already)
	return <XRController side="left" options={options}/>;
};
function XRController_Wrapped_Right() {
	const options = storeOpts.controller!["right"] as DefaultXRControllerOptions; // fsr we need to grab it ourselves from what we went initially... (I thought its data would be sent as props already)
	return <XRController side="right" options={options}/>;
};

/** If true, the room uses the single-blend export with Blender-baked lightmaps (@BakedLHQComps.tsx), rather than the old per-model glbs + runtime ProgressiveLightMap. */
export const useBakedLHQ = true;

export const sc = .03;
//export const sc = 1;
//export const sc = 33;

export type LHQInstance = {
	forceUpdate: ()=>any,
	useVR: boolean,
	usePaint: boolean,
	//initDone_time: number|n,
	mapControls: MapControls|n,
	canvas: ()=>(HTMLCanvasElement|n),
	tweenGroup: TWEEN.Group,
	paintableSurface_meshRef: React.RefObject<THREE.Mesh|null>|n,
	roomColliders_groupRef: React.RefObject<THREE.Group|null>|n,
	renderer: THREE.WebGLRenderer|n,
	lightmap: ProgressiveLightMap|n,
	lightmapState: LightmapState|n,
	lightmapObjects: THREE.Object3D[],
	dirLights: THREE.DirectionalLight[],
	/*possiblyReadyTime: number|n,
	markPossiblyReady: (name?: string)=>any,*/
	modelLoadStates: Map<string, ModelLoadState>,
	modelLoadsDoneTime: number|n,
	markModelLoadState: (id: string, state: ModelLoadState)=>any,
};
type ModelLoadState = "loading"|"loaded"|"removed";

export let LHQScene_latest = null as LHQInstance|n;
export const LHQScene = observer_mgl_r3({useAutoInvalidate: 0}, (props: {})=>{
	let {} = props;
	const uiState = store.main.tools.vr;
	const renderer = useThree(state=>state.gl);
	const scene = useThree(state=>state.scene);
	const camera = useThree(state=>state.camera);

	// clears the BakedLightmaps material-tracker at scene-mount, so the previous mount's materials don't pile up in it (the new mount re-registers its own as models load)
	useEffect(()=>{
		ClearBakedLightmapMats();
	}, []);

	// aim the realtime MainLight like the Blender sun the lightmaps were baked with (until loaded, it uses the hardcoded lightPositions_avg)
	const [bakedSun, setBakedSun] = useState<BakedSun|n>(null);
	useEffect(()=>{
		if (!useBakedLHQ) return;
		let cancelled = false;
		LoadBakedSun().then(sun=>{ if (!cancelled) setBakedSun(sun); });
		return ()=>{ cancelled = true; };
	}, []);
	const mainLightProps = useMemo(()=>{
		if (bakedSun == null) return {position: lightPositions_avg.To3JPos(), color: lightColor};
		// light is inside ZUpGroup, so the (z-up) direction applies as-is; target stays at the origin, so position = -dir * distance
		const dist = lightPositions_avg.To3JPos().length();
		return {
			position: new THREE.Vector3(...bakedSun.dir).multiplyScalar(-dist),
			color: new THREE.Color().setRGB(...bakedSun.color, THREE.LinearSRGBColorSpace),
		};
	}, [bakedSun]);

	// z-up content lives inside the ZUpGroup (see below); the scene ROOT stays y-up (matching WebXR + teleport/pointer systems).
	// note: we do NOT set camera.up to (0,0,1) anymore -- root space is y-up, so three's default up is correct here.
	const canvasRef = React.useRef<HTMLCanvasElement>(null!);
	//const cameraRef = React.useRef<THREE.OrthographicCamera>(null!);

	const forceUpdate = useForceUpdate();
	const self = useMemo(()=>({
		forceUpdate,
		useVR: InVRHeadset(), //|| true,
		usePaint: InVRHeadset() && !useBakedLHQ, // paint-mode disabled for baked mode (non-core; would need a runtime texture-format switch)
		initDone_time: null as number|n,
		mapControls: null as MapControls|n,
		canvas: (()=>null) as ()=>HTMLCanvasElement|n,
		tweenGroup: new TWEEN.Group(),
		paintableSurface_meshRef: null as React.RefObject<THREE.Mesh|null>|n,
		roomColliders_groupRef: null as React.RefObject<THREE.Group|null>|n,

		renderer: null as THREE.WebGLRenderer|n,
		lightmap: null as ProgressiveLightMap|n,
		lightmapState: null as LightmapState|n,
		lightmapObjects: [] as THREE.Object3D[],
		dirLights: [] as THREE.DirectionalLight[],

		modelLoadStates: new Map<string, "loading"|"loaded"|"removed">(),
		modelLoadsDoneTime: null as number|n,
		markModelLoadState(id: string, state: "loading"|"loaded"|"removed") {
			self.modelLoadStates.set(id, state);
			const modelsAreLoading = [...self.modelLoadStates.values()].some(a=>a == "loading" || a == "removed"); // count "removed" as loading (currently, no objects actually get removed, so that's just a re-render in progress)
			// once no models are loaded, set model-loads-done-time to now
			if (modelsAreLoading) {
				self.modelLoadsDoneTime = null;
			} else {
				self.modelLoadsDoneTime ??= Date.now();
			}
			console.log("ModelLoadStates:", JSON.stringify([...self.modelLoadStates]));
		},
	}), []);
	Object.assign(self, {
		forceUpdate,
		canvas: ()=>canvasRef.current,
		//camera: ()=>cameraRef.current,
		renderer, //scene, camera,
	});
	LHQScene_latest = self;
	g.LHQScene_latest = self;
	g._3JSState = useThree(); // for debugging purposes, so we can inspect the latest 3js state in console

	useEffect(()=>{
		if (store.main.testing.vrLightmap.autoLoadLightmapFromServer) {
			LoadLightmapFromServer(); // note: this is async
		}
	}, []);

	useSceneClickWithRaycastHandler({
		renderer, camera, scene,
		handler: (intersects, event)=>{
			// Log the name of the first hit object (if any)
			if (intersects.length == 0) return void console.log("No object hit");

			const hitObject = intersects[0].object;

			// Traverse up the parent hierarchy to find named objects
			let parent = hitObject.parent;
			const parentNames = [] as string[];
			while (parent) {
				parentNames.Insert(0, parent.name || "unnamed object");
				parent = parent.parent;
			}

			const objName = hitObject.name || "unnamed object";
			const path = [...parentNames, objName];
			console.log("Hit object name:", `${objName} (${path.join(" → ")})`, hitObject);
		},
	});

	const [_, setInitDone_copy] = useState(false); // we just use this to trigger a re-render once init is done, eg. for lhq-overlay (non-react-tree readers should check [self/lhq].initState instead)

	useFrame_lhqScene(self, setInitDone_copy);
	useFrame(()=>{
		// keep the ZUpGroup's inverse-world-matrix cache fresh, for use by ZUpGroupHolder.WorldPosToLocal (components convert world->local in their own useFrame hooks, which may run before/after this one -- the one-frame staleness is harmless for rendering)
		ZUpGroupHolder.RefreshWorldMatrixInverse();
	});

	const contents = <>
		{/*<color attach="background" args={[0, 0, 0]}/>*/}

		{/*<TweenUpdater tweenGroup={self.tweenGroup}/>*/}

		{/* ZUpGroup: everything inside treats the world as z-up (matching the z-up vertex data of /3D/ZUp models, and the Vec3-authored placements).
			The rotation converts content-up (0,0,1) -> root-up (0,1,0), since the scene-root/XR/teleport systems are y-up.
			Keep everything inside unless a component is deeply y-up-wired (currently only XROrigin, which must stay outside). */}
		<group name="ZUpGroup" rotation={[-Math.PI / 2, 0, 0]}
			ref={c=>{ ZUpGroupHolder.group = c; }}>
			<ambientLight name="AmbientLight" intensity={0.5}/>
			{/*<group name="SceneOrigin" position={[0, 0, 0]} rotation={[0, 0, 0]}>*/}
			<directionalLight name="MainLight"
				ref={c=>g.l1 = c}
				intensity={.5}
				//position={[...lightBase_windows.Plus(-.7, 0, 10).To3JPos().toArray()]}
				//position={lightPositions_avg.Minus(lightTargetPos).To3JPos()} // subtract target-pos, since light's target is 0,0,0
				position={mainLightProps.position} // from the Blender sun (manifest.json), once loaded; else lightPositions_avg
				//lookAt={new Vec3(0, 0, 0).To3JPos()}
				//target={new Vec3(0, 0, 0).To3JPos()}
				//rotation={[-Math.PI / 4, Math.PI / 4, 0]}
				color={mainLightProps.color}
				/*castShadow shadow-mapSize={[1024, 1024]} shadow-bias={-0.001}*//>


			<LHQOverlay lhq={self}/>

			{self.usePaint && <PaintController/>}

			<PaintableSurface lhq={self} usePaint={self.usePaint} useVR={self.useVR} position={new THREE.Vector3(0, 0, 0)} rotation={new THREE.Euler(0, 0, 0)}>
				{/*<CubeThing lhq={self}/>*/}
				{useBakedLHQ && <BakedLHQ lhq={self} full={LHQFull()}/>}
				{/* 3d-comps from @Simple3DComps.tsx */}
				{!useBakedLHQ && <>
					<LHQFloor lhq={self}/>
					<LHQWalls lhq={self}/>
					<LHQCeiling lhq={self}/>
					{LHQFull() && <>
						<LHQDoors lhq={self}/>
						<LHQWindows lhq={self}/>
						<LHQPainting lhq={self}/>
						<LHQDesksAndGlassWalls lhq={self}/>
						<LHQConferenceTable lhq={self}/>
						<LHQElectronics lhq={self}/>
						<LHQBookshelves lhq={self}/>
						<LHQBalcony lhq={self}/>
						<LHQSofaSet lhq={self}/>
					</>}
				</>}
			</PaintableSurface>

			{/*self.useVR && <YouTubePanel position={[5, 1.5, 0]} rotation={[0, 0, 0]}/>*/}
			<VRUIRoot/>
			{/*<WSReconnectUI_Temp/>*/}
			<SceneXREventListeners/>
			<DreamPanels lhq={self}/>
			<ImageFrames lhq={self}/>
			{(()=>{
				if (TestStr() == "water") return <WaterStreams lhq={self}/>;
				if (TestStr() == "spheres") return <ChaoticSphereBoundsSet lhq={self}/>;
				if (TestStr() == "coins") return <CoinCatcherSet lhq={self}/>;
				return <AlarmClockCatcherSet lhq={self}/>;
			})()}
			<RoomColliders lhq={self} showBounds={false}/>
		</group>

		{/* root-space (y-up) items below; must NOT be in the ZUpGroup */}

		<SkyBackground/>
		{/* Re-applies the lightmapIntensity setting to all baked materials whenever it changes (mounts within the reactive tree, so setting-changes invalidate it). */}
		<LightmapIntensityController/>

		<VRHeadsetStateTracker/>
		{/* debug probe for ImageFrames raycasting (mount outside ZUpGroup; world-space markers). enable with g.debugImageFramesRay = true; remove once resolved */}
		<ImageFramesDebugProbe/>
		{!self.useVR && <OrbitControls
			ref={c=>{
				if (!c) return;

				// far, back of room (root-space is y-up)
				c.object.position.set(4, 5, 1.3);
				c.target.set(-10, 0, 0);
			}}
			makeDefault
			enablePan={true}
			enableZoom={true}
			enableRotate={true}
			minDistance={1}
			maxDistance={100}
		/>}

		{self.useVR && <TeleportTarget onTeleport={newPos=>{
			RunInAction("LHQScene.TeleportTarget.onTeleport", ()=>uiState.SetUserPosition(Vec3.From3JPos(newPos)));
		}}>
			{/* root-space (y-up) floor -- classic y-up floor-plane idiom applies here */}
			{/*<mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
				<planeGeometry args={[30, 30]}/>
				<meshStandardMaterial {...teleportTargetMesh_sharedProps}/>
			</mesh>*/}
			{/* freeform ground-level floor: the whole building footprint (LHQ_21.blend exterior extents: blender x -17.15..55.95, y -33.45..11.95) + 1km out in each direction,
				so the user can go anywhere at ground-level (far-off shots, the unbuilt parts of the building, etc.) -- converted to root-space (x, z, -y) */}
			<mesh name="FreeformTeleportFloor" rotation={[-Math.PI / 2, 0, 0]} position={[(-17.15 + 55.95) / 2, 0, (-11.95 + 33.45) / 2]}>
				<planeGeometry args={[(55.95 + 17.15) + 2000, (11.95 + 33.45) + 2000]}/>
				<meshStandardMaterial {...teleportTargetMesh_sharedProps}/>
			</mesh>
			{/* loft (indoor balcony) floor (Loft_Floor slab top: blender z 3.4627; trapezoid with diagonal SW/SE ends, north edge = inner face of Loft_Wall_N at y -6.17).
				The shape is given in blender xy; the -90deg x-rotation maps local (x, y) to root-space (x, -y). */}
			<mesh name="LoftTeleportFloor" rotation={[-Math.PI / 2, 0, 0]} position={[0, 3.4627, 0]}>
				<shapeGeometry args={[loftTeleportFloorShape]}/>
				<meshStandardMaterial {...teleportTargetMesh_sharedProps}/>
			</mesh>
			{/* outdoor balcony floor, full length of the building (from LHQ_21.blend BalconyFloorTiled: blender x -13.7..52.5, y 7.6..10.563, z 0.03) -- converted to root-space (x, z, -y) */}
			{/* commented; covered by FreeformTeleportFloor above */}
			{/*<mesh name="OutdoorBalconyTeleportFloor" rotation={[-Math.PI / 2, 0, 0]} position={[(-13.7 + 52.5) / 2, 0.03, -(7.6 + 10.563) / 2]}>
				<planeGeometry args={[52.5 + 13.7, 10.563 - 7.6]}/>
				<meshStandardMaterial {...teleportTargetMesh_sharedProps}/>
			</mesh>*/}
		</TeleportTarget>}

		{/* XROrigin must stay OUTSIDE the ZUpGroup: XR poses are y-up, and XROrigin adopts the xr-camera as a child, so it maps XR space 1:1 onto root space. */}
		{self.useVR && <XROrigin position={new Vec3(uiState.GetUserPosition()).To3JPos()}/>}
	</>;

	if (self.useVR) {
		return <XR store={xrStore}>{contents}</XR>;
	}
	return contents;
});
/** Loft_Floor walkable top, in blender xy (counter-clockwise from above, so the face points +z = up after the mesh's rotation). */
const loftTeleportFloorShape = new THREE.Shape([
	new THREE.Vector2(-8.606, -6.17), // NW (W diagonal meets Loft_Wall_N)
	new THREE.Vector2(-6.357, -8.392), // SW
	new THREE.Vector2(5.098, -8.392), // SE
	new THREE.Vector2(7.053, -6.17), // NE (E diagonal meets Loft_Wall_N)
]);
const teleportTargetMesh_sharedProps = {
	transparent: true, opacity: 0, color: "#FFFFFF",
	// depthWrite/colorWrite off: an opacity-0 plane still writes depth by default, which (depending on transparent-object sort order) hid the Ext_UpWin* glass below elevation=0
	depthWrite: false, colorWrite: false,
};

/** Re-applies the lightmapIntensity setting to all baked materials whenever it changes. Renders null; mounts inside the LHQ scene. */
const LightmapIntensityController = observer_mgl_r3({useAutoInvalidate: 0}, ()=>{
	const lightmapIntensity = store.main.tools.vr.scene_lhq.lightmapIntensity; // read for reactivity
	useEffect(()=>{
		ApplyLightmapIntensity();
	}, [lightmapIntensity]);
	return null;
});